import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, Chip, Divider, Stack, Switch, Typography } from '@mui/material';
import { EditOutlined, MarkEmailReadOutlined, VisibilityOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { emailService } from '../../services/emailService';
import type { EmailSettings, EmailTypeSetting } from '../../types/email';
import { SectionCard } from '../common/SectionCard';
import EmptyState from '../common/EmptyState';
import EmailEditDialog from './EmailEditDialog';
import { isEdited } from './emailCopy';

interface Props {
  settings: EmailSettings;
  writable: boolean;
  onChange: (next: EmailSettings) => void;
}

const EmailTypeRow: React.FC<{
  type: EmailTypeSetting;
  writable: boolean;
  saving: boolean;
  onToggle: (type: EmailTypeSetting, enabled: boolean) => void;
  onPreview: (type: EmailTypeSetting) => void;
}> = React.memo(({ type, writable, saving, onToggle, onPreview }) => (
  <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1.5 }}>
    <Switch
      checked={type.enabled} disabled={!writable || saving} onChange={(_, v) => onToggle(type, v)}
      inputProps={{ 'aria-label': type.label }} sx={{ ml: -1 }}
    />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" fontWeight={600} color={type.enabled ? 'text.primary' : 'text.secondary'}>{type.label}</Typography>
        {isEdited(type) && <Chip label="Your wording" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', color: 'text.secondary' }} />}
      </Stack>
      <Typography variant="body2" color="text.secondary">{type.description}</Typography>
    </Box>
    <Button size="small" startIcon={writable ? <EditOutlined sx={{ fontSize: 16 }} /> : <VisibilityOutlined sx={{ fontSize: 16 }} />} onClick={() => onPreview(type)}
      sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0, color: 'text.secondary' }}>
      {writable ? 'Edit' : 'Preview'}
    </Button>
  </Stack>
));

/** The emails families can get, one card per module, each switched on or off by the school. */
const EmailTypesCard: React.FC<Props> = ({ settings, writable, onChange }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<EmailTypeSetting | null>(null);

  const groups = useMemo(() => settings.types.reduce<Array<{ name: string; types: EmailTypeSetting[] }>>((acc, t) => {
    const last = acc[acc.length - 1];
    if (last?.name === t.group) last.types.push(t); else acc.push({ name: t.group, types: [t] });
    return acc;
  }, []), [settings.types]);

  const toggle = useCallback(async (type: EmailTypeSetting, enabled: boolean) => {
    setSavingKey(type.key);
    try {
      onChange(await emailService.updateSettings({ types: [{ type: type.key, enabled }] }));
      enqueueSnackbar(`${type.label} emails ${enabled ? 'on' : 'off'}`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Couldn’t save', { variant: 'error' });
    } finally {
      setSavingKey(null);
    }
  }, [onChange, enqueueSnackbar]);

  const preview = useCallback((type: EmailTypeSetting) => setPreviewing(type), []);

  return (
    <>
      {groups.length === 0 ? (
        <EmptyState icon={<MarkEmailReadOutlined />} title="No emails available" description="Your plan doesn’t include a module that emails families yet." />
      ) : groups.map(g => {
        const on = g.types.filter(t => t.enabled).length;
        return (
          <SectionCard
            key={g.name}
            title={`${g.name} emails`}
            action={<Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>{on} of {g.types.length} on</Typography>}
          >
            {g.types.map((t, i) => (
              <React.Fragment key={t.key}>
                {i > 0 && <Divider />}
                <EmailTypeRow type={t} writable={writable} saving={savingKey === t.key} onToggle={toggle} onPreview={preview} />
              </React.Fragment>
            ))}
          </SectionCard>
        );
      })}

      {previewing && (
        <EmailEditDialog
          type={previewing}
          writable={writable}
          onClose={() => setPreviewing(null)}
          onSaved={(next) => { onChange(next); setPreviewing(null); }}
        />
      )}
    </>
  );
};

export default EmailTypesCard;
